import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Shield } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import "./HeroBranchingSystem.css";

const LOGO_LIGHT_TEXT = `${process.env.PUBLIC_URL}/images/logos/Max_Logo_Dark_01.png`;
const LOGO_DARK_TEXT = `${process.env.PUBLIC_URL}/images/logos/Max_Logo_01.png`;

// Explicit sequence timing for a step-by-step reveal:
const PILLARS = [
  { 
    id: "defect", 
    label: "ZERO DEFECT", 
    Icon: CheckCircle2,
    x: 100, 
    y: 420,
    delay: 0.8 
  },
  { 
    id: "downtime", 
    label: "ZERO DOWNTIME", 
    Icon: Clock,
    x: 300, 
    y: 420,
    delay: 2.2 
  },
  { 
    id: "accident", 
    label: "ZERO ACCIDENT", 
    Icon: Shield,
    x: 500, 
    y: 420, 
    delay: 3.6
  },
];

export const HeroBranchingSystem = () => {
  const { theme } = useTheme();
  
  const currentLogo = theme === "dark" ? LOGO_LIGHT_TEXT : LOGO_DARK_TEXT;
  const beamColor = theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.2)";
  const beamColorFull = theme === "dark" ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 0.4)";
  const pulseColor = theme === "dark" ? "white" : "#00A3FF";
  const pathBaseColor = theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";

  return (
    <div className="hero-branch-wrapper">
      <svg 
        className="hero-branch-svg-full" 
        viewBox="0 0 600 600" 
        fill="none" 
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="svg-beam-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="50%" stopColor={beamColor} />
            <stop offset="100%" stopColor={beamColorFull} />
          </linearGradient>
        </defs>

        {/* Connection Paths */}
        {PILLARS.map((p) => {
          const path = `M 300 90 C 300 240, ${p.x === 300 ? 300.01 : p.x} 240, ${p.x} 400`;
          
          return (
            <React.Fragment key={`branch-${p.id}`}>
              <motion.path 
                d={path} 
                stroke={pathBaseColor} 
                strokeWidth="1.5" 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              />
              
              <motion.path
                d={path}
                stroke="url(#svg-beam-gradient)"
                strokeWidth="4"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ 
                  duration: 1.2, 
                  delay: p.delay, 
                  ease: "easeInOut" 
                }}
              />

              <motion.circle
                r="5"
                fill={pulseColor}
                style={{ 
                  offsetPath: `path("${path}")`,
                  offsetRotate: "auto",
                  filter: theme === "dark" ? "drop-shadow(0 0 10px white) drop-shadow(0 0 5px #00A3FF)" : "drop-shadow(0 0 5px rgba(0, 163, 255, 0.4))"
                }}
                initial={{ offsetDistance: "0%", opacity: 0 }}
                animate={{ 
                  offsetDistance: ["0%", "100%"],
                  opacity: [0, 1, 1, 0]
                }}
                transition={{ 
                  duration: 2.5, 
                  delay: p.delay + 1, 
                  repeat: Infinity, 
                  ease: "linear" 
                }}
              />
            </React.Fragment>
          );
        })}

        {/* --- NODES --- */}
        <foreignObject x="190" y="0" width="220" height="120">
          <motion.div 
            className="branch-svg__root-node"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: "backOut" }}
          >
            <div className="branch-svg__logo-box branch-svg__logo-box--lg">
              <img src={currentLogo} alt="Logo" className="branch-svg__logo" />
            </div>
          </motion.div>
        </foreignObject>

        {/* 2. PILLAR NODES */}
        {PILLARS.map((p) => (
          <foreignObject 
            key={`node-${p.id}`} 
            x={p.x - 100} 
            y={p.y - 40} 
            width="200" 
            height="180"
          >
            <motion.div
              className="branch-svg__pillar-node"
              style={{ paddingTop: '40px' }}
              initial={{ scale: 0, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.5, 
                delay: p.delay + 1,
                type: "spring",
                stiffness: 150
              }}
            >
              <div className="branch-svg__pill branch-svg__pill--lg">
                <p.Icon size={20} strokeWidth={2} className="branch-svg__pill-icon" />
                <span className="branch-svg__pill-label">{p.label}</span>
              </div>
            </motion.div>
          </foreignObject>
        ))}
      </svg>
    </div>
  );
};
